import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";
import {
    Card,
    CardBody,
    Row,
    Col,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
} from "reactstrap";
import { ChromePicker } from "react-color";
import classNames from "classnames";
import { useGetStatuses, useCreateStatus, useUpdateStatus } from "api/inventory-status/service";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { useTheme2FormPage, Theme2FormFieldsStyle, THEME2_ADMIN_INSETS } from "hooks/useTheme2FormPage";
import SubHeader from "components/Navbars/SubHeader";

const InventoryStatusAdd = ({
    activeColor = "secondary",
    shellThemeVariant = "1",
    isLightTheme = false,
}) => {
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const navigate = useNavigate();
    const createStatusMutation = useCreateStatus();
    const updateStatusMutation = useUpdateStatus();
    const { data: statusesData, isLoading: isLoadingStatuses } = useGetStatuses(undefined, { enabled: isEditMode });

    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const {
        isTheme2,
        inputStyle,
        inputClassName,
        theme2FieldsCss: baseTheme2FieldsCss,
        theme2BorderColor: colorSwatchBorderColor,
        contentClassName,
        formFieldsClassName,
    } = useTheme2FormPage({
        shellThemeVariant,
        pageKey: "inventory-status-add",
        insetClass: THEME2_ADMIN_INSETS.inventoryParameters,
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

    const theme2FieldsCss = useMemo(
        () =>
            isTheme2
                ? `${baseTheme2FieldsCss}
                  @media (max-width: 991.98px) {
                    .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.inventory-status-add-page.inventory-status-add-page--theme2.theme2-admin-content-compact.theme2-inventory-parameters-inset {
                      padding-top: calc(74px + 45px) !important;
                    }
                  }
                  @media (max-width: 575.98px) {
                    .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.inventory-status-add-page.inventory-status-add-page--theme2.theme2-admin-content-compact.theme2-inventory-parameters-inset {
                      padding-top: calc(64px + 45px) !important;
                    }
                  }`
                : "",
        [isTheme2, baseTheme2FieldsCss]
    );
    const { t } = useTranslation();
    const { showNotification } = useNotification();

    // Protected status keys that cannot be edited
    const protectedStatusKeys = ['available', 'sold', 'deleted'];

    const isKeyProtected = () => {
        const currentKey = editForm.key?.toLowerCase();
        return protectedStatusKeys.includes(currentKey);
    };

    const [editForm, setEditForm] = useState({
        name: '',
        key: '',
        color: '#007bff',
        is_active: true,
    });
    const [errors, setErrors] = useState({});
    const [showColorPicker, setShowColorPicker] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 992);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const subHeaderTitle = useMemo(() => {
        if (isEditMode) {
            return editForm.name || editForm.key || id;
        }
        return t("inventoryStatusAdd.titleAdd");
    }, [isEditMode, editForm.name, editForm.key, id, t]);

    const subHeaderStatus = useMemo(() => {
        if (!isEditMode) return "";
        return editForm.is_active
            ? t("common.active")
            : t("common.inactive");
    }, [isEditMode, editForm.is_active, t]);

    const subHeaderStatusColor = useMemo(() => {
        if (!isEditMode) return undefined;
        if (editForm.color) return editForm.color;
        return editForm.is_active ? "#28a745" : "#dc3545";
    }, [isEditMode, editForm.color, editForm.is_active]);

    useEffect(() => {
        if (isEditMode && statusesData && statusesData.results) {
            // Find the status by id (id or documentId)
            const status = statusesData.results.find(s => String(s.documentId) === String(id));
            if (status) {
                setEditForm({
                    name: status.status_name || status.name || '',
                    key: status.status_key || status.key || '',
                    color: status.color || '#007bff',
                    is_active: status.is_active !== undefined ? status.is_active : true,
                });
            }
        }
    }, [isEditMode, statusesData, id]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditForm(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleColorChange = (color) => {
        setEditForm(prev => ({ ...prev, color: color.hex }));
    };

    const validateForm = () => {
        const newErrors = {};
        const missingFields = [];
        
        if (!editForm.name.trim()) {
            newErrors.name = t('inventoryStatusAdd.statusNameRequired');
            missingFields.push(t('inventoryStatusAdd.statusName'));
        }
        if (!editForm.key.trim()) {
            newErrors.key = t('inventoryStatusAdd.statusKeyRequired');
            missingFields.push(t('inventoryStatusAdd.statusKey'));
        }
        
        setErrors(newErrors);
        
        // Show notification if there are errors
        if (Object.keys(newErrors).length > 0) {
            const fieldsList = missingFields.join(', ');
            showNotification(
                'danger',
                `${t('inventoryStatusAdd.requiredFieldsMissing')}: ${fieldsList}`
            );
            return false;
        }
        
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        
        try {
            if (isEditMode) {
                await updateStatusMutation.mutateAsync({
                    id,
                    data: {
                        name: editForm.name,
                        key: editForm.key,
                        color: editForm.color,
                        is_active: editForm.is_active,
                    },
                });
            } else {
                await createStatusMutation.mutateAsync({
                    name: editForm.name,
                    key: editForm.key,
                    color: editForm.color,
                    is_active: editForm.is_active,
                });
            }
        } catch (error) {
            // Error handling is now done in the service layer
            console.error("Form submission error:", error);
        }
    };

    const handleCancel = () => {
        navigate('/inventory-parameters?tab=status');
    };

    return (
        <div
            className={contentClassName}
            style={{
                minHeight: "80vh",
                paddingTop: isMobile && !isTheme2 ? "7rem" : undefined,
            }}
        >
            <Theme2FormFieldsStyle css={theme2FieldsCss} />
            <div style={{ width: "100%" }}>
                <SubHeader
                    activeColor={activeColor}
                    back={-1}
                    backFallback="/inventory-parameters?tab=status"
                    title={subHeaderTitle}
                    status={subHeaderStatus}
                    statusColor={subHeaderStatusColor}
                    theme2ScrollGapFill={isTheme2}
                    isLightTheme={isLightTheme}
                />
            <Row>
                <Col md="12">
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit} className={formFieldsClassName}>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="name" className="form-control-label">
                                                {t('inventoryStatusAdd.statusName')} <span style={{ color: 'red' }}>*</span>
                                            </Label>
                                            <Input
                                                id="name"
                                                name="name"
                                                type="text"
                                                value={editForm.name}
                                                onChange={handleInputChange}
                                                className={inputClassName(errors.name)}
                                                style={inputStyle}
                                                placeholder={t('inventoryStatusAdd.enterStatusName')}
                                                disabled={createStatusMutation.isPending || updateStatusMutation.isPending}
                                            />
                                            {errors.name && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.name}
                                                </div>
                                            )}
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="key" className="form-control-label">
                                                {t('inventoryStatusAdd.statusKey')} <span style={{ color: 'red' }}>*</span>
                                            </Label>
                                            <Input
                                                id="key"
                                                name="key"
                                                type="text"
                                                value={editForm.key}
                                                onChange={handleInputChange}
                                                className={inputClassName(errors.key)}
                                                style={inputStyle}
                                                placeholder={t('inventoryStatusAdd.enterStatusKey')}
                                                disabled={createStatusMutation.isPending || updateStatusMutation.isPending || (isEditMode && isKeyProtected())}
                                            />
                                            {errors.key && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.key}
                                                </div>
                                            )}
                                            {isEditMode && isKeyProtected() && (
                                                <small className="text-muted">
                                                    {t('inventoryStatusAdd.keyCannotBeChanged')}
                                                </small>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row className="align-items-center">
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="color" className="form-control-label">
                                                {t('inventoryStatusAdd.statusColor')}
                                            </Label>
                                            <div style={{ position: 'relative' }}>
                                                <div
                                                    className="inventory-status-color-swatch"
                                                    style={{
                                                        width: "100%",
                                                        height: "38px",
                                                        backgroundColor: editForm.color,
                                                        border: `1px solid ${colorSwatchBorderColor}`,
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        textShadow: "1px 1px 1px rgba(0,0,0,0.5)",
                                                        ...(isTheme2 ? { fontSize: "14px" } : {}),
                                                    }}
                                                    onClick={() => setShowColorPicker(!showColorPicker)}
                                                >
                                                    {editForm.color}
                                                </div>
                                                {showColorPicker && (
                                                    <div style={{ position: 'absolute', zIndex: 1000, top: '100%', left: 0, marginTop: '5px' }}>
                                                        <ChromePicker
                                                            color={editForm.color}
                                                            onChange={handleColorChange}
                                                        />
                                                        <div
                                                            style={{
                                                                position: 'fixed',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                zIndex: -1
                                                            }}
                                                            onClick={() => setShowColorPicker(false)}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup check>
                                            <Label check className="mb-0 d-flex align-items-center">
                                                <Input
                                                    type="checkbox"
                                                    id="is_active"
                                                    name="is_active"
                                                    className="form-check-input"
                                                    checked={!!editForm.is_active}
                                                    onChange={handleInputChange}
                                                    disabled={createStatusMutation.isPending || updateStatusMutation.isPending}
                                                />
                                                <span className="form-check-sign">
                                                    <span className="check" />
                                                </span>
                                                <span style={{ marginLeft: 8 }}>{t('inventoryStatusAdd.activeStatus')}</span>
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <div className="d-none d-lg-block mt-4" />
                            </Form>
                            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-end">
                                <div className="d-none d-lg-flex align-items-center">
                                    <Button
                                        color="secondary"
                                        outline
                                        size="sm"
                                        onClick={handleCancel}
                                        disabled={createStatusMutation.isPending || updateStatusMutation.isPending}
                                        className="mr-2"
                                    >
                                        {t("inventoryStatusAdd.cancel")}
                                    </Button>
                                    <Button
                                        style={{ ...activeColorStyle, color: "white" }}
                                        size="sm"
                                        onClick={handleSubmit}
                                        disabled={createStatusMutation.isPending || updateStatusMutation.isPending}
                                    >
                                        {createStatusMutation.isPending || updateStatusMutation.isPending ? (
                                            <>
                                                <Spinner size="sm" className="mr-1" />
                                                {isEditMode
                                                    ? t("inventoryStatusAdd.updating")
                                                    : t("inventoryStatusAdd.creating")}
                                            </>
                                        ) : (
                                            <>
                                                <i
                                                    className={
                                                        isEditMode
                                                            ? "tim-icons icon-pencil"
                                                            : "tim-icons icon-simple-add"
                                                    }
                                                    style={{ marginRight: "8px" }}
                                                />
                                                {isEditMode
                                                    ? t("inventoryStatusAdd.updateStatus")
                                                    : t("inventoryStatusAdd.createStatus")}
                                            </>
                                        )}
                                    </Button>
                                </div>

                                <div className="d-flex d-lg-none flex-column w-100 mt-3">
                                    <Button
                                        color="secondary"
                                        outline
                                        onClick={handleCancel}
                                        disabled={createStatusMutation.isPending || updateStatusMutation.isPending}
                                        className="mb-2 w-100"
                                        style={{ padding: "12px" }}
                                    >
                                        {t("inventoryStatusAdd.cancel")}
                                    </Button>
                                    <Button
                                        style={{ ...activeColorStyle, color: "white", padding: "12px" }}
                                        onClick={handleSubmit}
                                        disabled={createStatusMutation.isPending || updateStatusMutation.isPending}
                                        className="w-100"
                                    >
                                        {createStatusMutation.isPending || updateStatusMutation.isPending ? (
                                            <>
                                                <Spinner size="sm" className="mr-1" />
                                                {isEditMode
                                                    ? t("inventoryStatusAdd.updating")
                                                    : t("inventoryStatusAdd.creating")}
                                            </>
                                        ) : (
                                            <>
                                                <i
                                                    className={
                                                        isEditMode
                                                            ? "tim-icons icon-pencil"
                                                            : "tim-icons icon-simple-add"
                                                    }
                                                    style={{ marginRight: "8px" }}
                                                />
                                                {isEditMode
                                                    ? t("inventoryStatusAdd.updateStatus")
                                                    : t("inventoryStatusAdd.createStatus")}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            </div>
        </div>
    );
};

export default InventoryStatusAdd; 